/**
 * PMTiles protocol registration for MapLibre GL JS.
 *
 * pmtiles:// URLs are resolved client-side with HTTP range requests, so the
 * tile archives can live on any static CDN without a tile server.
 *
 * This module is idempotent: registering twice is a no-op, which matters
 * under React 18 StrictMode double-mounting in dev.
 */
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';

const PROTOCOL_NAME = 'pmtiles';
let registered = false;

/**
 * Build the archive URL for the blocks dataset.
 *
 * Resolution order:
 *  1. tenant.map.tiles_url (absolute, per-tenant override)
 *  2. {VITE_TILES_BASE_URL}/{dataset_hash}/blocks.pmtiles
 *
 * Throws when neither is configured — callers should surface this as a
 * configuration error, not silently render an empty map.
 */
export function resolveBlocksTilesUrl(options: {
  tilesUrl?: string;
  datasetHash?: string;
}): string {
  if (options.tilesUrl && options.tilesUrl.trim()) {
    return rewriteLocalhostTilesUrl(options.tilesUrl.trim());
  }
  const base = (import.meta.env.VITE_TILES_BASE_URL ?? '').trim().replace(/\/+$/, '');
  const hash = (options.datasetHash ?? '').trim();
  if (base && hash) {
    return rewriteLocalhostTilesUrl(`${base}/${hash}/blocks.pmtiles`);
  }
  throw new Error(
    'No block tile source configured: tenant config must provide map.tiles_url ' +
      'or map.dataset_hash, or VITE_TILES_BASE_URL must be set.',
  );
}

/**
 * Seed data points tiles at localhost:8080. On a public IP deploy the
 * visitor's browser is not that host — rewrite to the page origin so
 * nginx can proxy `/tiles/` to the tiles service.
 */
function rewriteLocalhostTilesUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  const pageHost = window.location.hostname;
  if (pageHost === 'localhost' || pageHost === '127.0.0.1') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return url;
    }
    parsed.protocol = window.location.protocol;
    parsed.hostname = pageHost;
    parsed.port = window.location.port;
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Register the pmtiles:// protocol with MapLibre (safe to call repeatedly). */
export function registerPmtilesProtocol(): void {
  if (registered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol(PROTOCOL_NAME, protocol.tile);
  registered = true;
}

/** Remove the protocol registration (used in tests / full teardown). */
export function unregisterPmtilesProtocol(): void {
  if (!registered) return;
  maplibregl.removeProtocol(PROTOCOL_NAME);
  registered = false;
}
